import {
    type ColorRepresentation,
    type OrthographicCamera,
    type Renderer,
    type Scene,
} from 'three';
import { type ElementSize } from '@vueuse/core';
import { cameraState, type CameraState } from '@/lib/threeScene/camera';
import { SCENE_BACKGROUND } from '@/plugins/formusTheme';

/**
 * Three.js renderer clear-colour. Shares its value with the `scene-background`
 * theme entry (see `plugins/formusTheme.ts`) so CSS gaps adjacent to the canvas
 * stay in sync with the 3D backdrop.
 */
export const DEFAULT_CLEAR_COLOR = SCENE_BACKGROUND;

type BeforeRenderCallback = () => void;

/**
 * Reactive state for rendering a 3D view.
 *
 * Used in conjunction with {@link useScene} + {@link attachRenderingState}
 * for scenes built on the animated `CameraState` system.
 */
export type RenderingState = {
    readonly camera: CameraState;
    clearColor: ColorRepresentation;

    /**
     * Allow cross-section planes by enabling local clipping:
     * https://threejs.org/docs/#api/en/renderers/WebGLRenderer.localClippingEnabled
     */
    localClippingEnabled: boolean;
    viewportSize: ElementSize | null;
};

export function renderingState(properties?: Partial<RenderingState>): RenderingState {
    return {
        camera: properties?.camera ?? cameraState(),
        clearColor: properties?.clearColor ?? DEFAULT_CLEAR_COLOR,
        localClippingEnabled: properties?.localClippingEnabled ?? true,
        viewportSize: null,
    };
}

/**
 * Rendering-context shape for the generic camera system. Consumed by
 * `updateCamera`, `updateViewport`, and `attachRenderingState`.
 */
export type RenderingContext = Readonly<{
    container: HTMLElement;
    canvas: HTMLCanvasElement;
    renderer: Renderer;
    scene: Scene;
    camera: OrthographicCamera;
    state: RenderingState;
    beforeRender: (callback: BeforeRenderCallback) => void;
}>;
